import { useCallback, useEffect, useRef, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { MotionReelCard } from '@/components/gallery/MotionReelCard'
import {
  nailsInMotionVideos,
  type NailsInMotionVideo,
} from '@/content/nailsInMotionVideos'
import { smoothEase } from '@/lib/motion'
import { cn } from '@/lib/utils'

const HERO_SPANS = ['tall', 'cell', 'cell'] as const

type HeroMotionCollageProps = {
  /** First three clips fill the bento layout (tall + two cells). Defaults to the first three entries in `nailsInMotionVideos`. */
  videos?: readonly NailsInMotionVideo[]
}

/**
 * Home hero right column: same motion-reel behavior as `NailsInMotionStrip` (muted loop in view,
 * tap / focus for sound, dims siblings when one is audible), in the original 2×2 bento grid.
 */
export function HeroMotionCollage({ videos }: HeroMotionCollageProps) {
  const reduced = useReducedMotion()
  const sectionRootRef = useRef<HTMLDivElement>(null)
  const [stripAudible, setStripAudible] = useState(true)
  const [audibleReelId, setAudibleReelId] = useState<string | null>(null)

  const handleAudibleChange = useCallback((id: string, audible: boolean) => {
    setAudibleReelId((prev) => (audible ? id : prev === id ? null : prev))
  }, [])

  useEffect(() => {
    const el = sectionRootRef.current
    if (!el) return
    const io = new IntersectionObserver(
      ([e]) => {
        if (!e) return
        const on = e.isIntersecting && e.intersectionRatio >= 0.05
        setStripAudible(on)
      },
      { threshold: [0, 0.02, 0.05, 0.08, 0.12, 0.2, 0.35] }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  const trio = (videos ?? nailsInMotionVideos).slice(0, 3)
  if (trio.length < 3) return null

  return (
    <div ref={sectionRootRef} className="relative min-h-0">
      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        {trio.map((item, i) => {
          const span = HERO_SPANS[i]
          return (
            <motion.figure
              key={item.id}
              initial={reduced ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                duration: reduced ? 0 : 0.45,
                delay: reduced ? 0 : 0.06 + i * 0.06,
                ease: smoothEase,
              }}
              className={cn(
                'm-0 flex min-h-0 flex-col',
                span === 'tall' && 'row-span-2 h-full min-h-[180px] md:min-h-[min(100%,320px)]',
                span === 'cell' && 'aspect-[4/5] w-full md:aspect-square'
              )}
            >
              <div
                className={cn(
                  'group/card relative min-h-0 flex-1 overflow-hidden rounded-[var(--radius-button)] transition-shadow duration-300 ease-out motion-reduce:transition-none',
                  !reduced &&
                    audibleReelId != null &&
                    audibleReelId !== item.id &&
                    'after:pointer-events-none after:absolute after:inset-0 after:z-[1] after:bg-black/55 after:transition-opacity after:duration-300 motion-reduce:after:transition-none',
                  !reduced && audibleReelId === item.id && 'relative z-[2]'
                )}
              >
                <MotionReelCard
                  reelId={item.id}
                  src={item.src}
                  poster={item.poster}
                  description={item.description}
                  reduced={!!reduced}
                  stripAudible={stripAudible}
                  onAudibleChange={handleAudibleChange}
                  fillHeight
                />
              </div>
            </motion.figure>
          )
        })}
      </div>
    </div>
  )
}
